import { Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { regex } from '@hero/schemas';

import * as S from './style';

const PasswordInput = ({
  onChange,
  value,
  name,
  showPasswordBox,
  placeholder,
  hasError,
  disabled = false,
  helperText
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordBoxVisible, setIsPasswordBoxVisible] = useState(false);
  const { t } = useTranslation();

  const validations = useMemo(() => {
    return {
      primary: [
        {
          isValid: value?.length > 8,
          message: t('atLeast8Chars')
        }
      ],
      secondary: [
        {
          isValid: regex.isLowerCase.test(value),
          message: t('lowerCaseLetters')
        },
        {
          isValid: regex.isUpperCase.test(value),
          message: t('upperCaseLetters')
        },
        {
          isValid: regex.isNumber.test(value),
          message: t('numbers')
        },
        {
          isValid: regex.isSpecialChar.test(value),
          message: t('specialChars')
        }
      ]
    };
  }, [value]);

  const MIN_FOLLOWING_VALIDATIONS = 3;
  const primaryErrors = validations.primary.filter(({ isValid }) => !isValid);
  const secondaryErrors = validations.secondary.filter(({ isValid }) => !isValid);
  const allErrors = [...primaryErrors, ...secondaryErrors];
  const isMin3FollowingValid =
    secondaryErrors.length <= secondaryErrors.length - MIN_FOLLOWING_VALIDATIONS;
  const hasLocalError = validations.primary.some(({ isValid }) => isValid) || !isMin3FollowingValid;

  return (
    <S.PasswordInput>
      <S.PasswordInputWrapper>
        <Input
          hasError={hasError}
          onFocus={() => setIsPasswordBoxVisible(true)}
          onClick={() => setIsPasswordBoxVisible(true)}
          onBlur={() => setIsPasswordBoxVisible(false)}
          onChange={onChange}
          inputProps={{
            maxLength: 45
          }}
          name={name}
          variant="outlined"
          label={t(placeholder ?? 'password')}
          type={isPasswordVisible ? 'text' : 'password'}
          value={value}
          autoComplete="off"
          disabled={disabled}
          error={hasError && hasLocalError}
          helperText={helperText || (hasError && hasLocalError ? allErrors?.[0]?.message : '')}
        />
        <S.PasswordEyeIcon
          onClick={() => setIsPasswordVisible(prev => !prev)}
          variant="text"
          color="secondary"
        >
          <Icon name={isPasswordVisible ? 'close' : 'preview'} />
        </S.PasswordEyeIcon>
        {!!value && showPasswordBox && (
          <S.PasswordStrength>{hasError ? t('weak') : t('strong')}</S.PasswordStrength>
        )}
      </S.PasswordInputWrapper>

      {showPasswordBox && isPasswordBoxVisible && (
        <S.PasswordArea style={{ marginTop: hasError && hasLocalError ? 4 : 0 }}>
          <Typography variant="body2">{t('passwordMustContain')}</Typography>
          <ul>
            {validations.primary.map(({ isValid, message }) => (
              <S.PasswordAreaItem isValid={isValid} key={message}>
                <Typography variant="caption1" color="inherit">
                  {message}
                </Typography>
              </S.PasswordAreaItem>
            ))}
            <S.PasswordAreaItem isValid={isMin3FollowingValid}>
              <Typography variant="caption1" color="inherit">
                {t('min3Following')}:
              </Typography>
            </S.PasswordAreaItem>
            <S.PasswordAreaItemList>
              {validations.secondary.map(({ isValid, message }) => (
                <S.PasswordAreaItem isValid={isValid} key={message}>
                  <Typography variant="caption1" color="inherit">
                    {message}
                  </Typography>
                </S.PasswordAreaItem>
              ))}
            </S.PasswordAreaItemList>
          </ul>
        </S.PasswordArea>
      )}
    </S.PasswordInput>
  );
};

PasswordInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  name: PropTypes.string,
  showPasswordBox: PropTypes.bool,
  placeholder: PropTypes.string,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string
};

export { PasswordInput };
